import React, { type ReactNode, type PropsWithChildren } from 'react';
import { Flex, xcss, Text, Box } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import Tooltip from '@atlaskit/tooltip';
import { TaskItemCard } from '../card';

type TaskItemProps = {
	taskInfoContent: ReactNode;
	children?: ReactNode;
};

type TooltipWrapperProps = {
	tooltip?: ReactNode;
};

type TaskInfoItemProps = {
	text: string;
	textMaxLines?: number;
	tooltip?: string;
	hasDivider?: boolean;
	Icon?: React.FC;
};

const TooltipWrapper = ({ children, tooltip }: PropsWithChildren<TooltipWrapperProps>) => {
	if (!tooltip) {
		return <>{children}</>;
	}

	return (
		<Tooltip content={tooltip} position="bottom">
			{children}
		</Tooltip>
	);
};

export const TaskInfoItem = ({
	text,
	textMaxLines = 1,
	tooltip,
	hasDivider = false,
	Icon,
}: TaskInfoItemProps) => (
	<Flex xcss={[hasDivider && taskInfoItemDivider]}>
		<TooltipWrapper tooltip={tooltip}>
			{Icon && (
				<Box xcss={infoIconWrapperStyles}>
					<Icon />
				</Box>
			)}
			<Text maxLines={textMaxLines} size="small" color="color.text.subtle">
				{text}
			</Text>
		</TooltipWrapper>
	</Flex>
);

export const TaskItem = ({ taskInfoContent, children }: TaskItemProps) => (
	<TaskItemCard>
		<Flex direction="row" alignItems="center" gap="space.050">
			<Flex xcss={leftContentFlexStyles}>{taskInfoContent}</Flex>
		</Flex>
		{children}
	</TaskItemCard>
);

const leftContentFlexStyles = xcss({
	flex: '4 1 0',
	[media.above.md]: {
		flex: '2 1 0',
	},
});

const taskInfoItemDivider = xcss({
	'::before': {
		content: '"•"',
		marginRight: 'space.050',
		color: 'color.text.subtle',
	},
});

const infoIconWrapperStyles = xcss({
	marginRight: 'space.025',
});
